import type { CSSProperties } from 'react'

/**
 * ILMU logo mark (flower only, no wordmark).
 *
 * Source: ytlailabs.com brand kit (Ilmu_Logo_03-Append-4C_Horz.svg, mark sub-group).
 * Renders inline SVG with the four brand colors:
 *   --ilmu-blue       #0048cc  (primary)
 *   --ilmu-dark-blue  #00205b  (deep)
 *   --ilmu-petal      #ffffff  (white petals — may merge with light surfaces)
 *   --ilmu-grey       #bfbfbf  (mid-tone)
 *
 * Used as the AI-assistant glyph across the app, replacing the
 * generic Material Symbols `auto_awesome` mark. Default size 24 —
 * smaller sizes are not recommended as the 4-color flower becomes
 * illegible noise below ~20px.
 */

interface IlmuMarkProps {
  size?: number
  className?: string
  style?: CSSProperties
  'aria-hidden'?: boolean | 'true' | 'false'
  'aria-label'?: string
  'data-testid'?: string
}

const BRAND = {
  blue: '#0048cc',
  darkBlue: '#00205b',
  petal: '#ffffff',
  grey: '#bfbfbf',
} as const

export function IlmuMark({
  size = 24,
  className,
  style,
  'aria-hidden': ariaHidden,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: IlmuMarkProps): JSX.Element {
  const hasLabel = typeof ariaLabel === 'string' && ariaLabel.length > 0
  const role = hasLabel ? 'img' : undefined
  const computedAriaHidden = ariaHidden ?? (hasLabel ? undefined : true)

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 58 57.63"
      width={size}
      height={size}
      role={role}
      aria-label={ariaLabel}
      aria-hidden={computedAriaHidden}
      className={className}
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...style }}
      data-testid={dataTestId}
    >
      <g>
        <polygon fill={BRAND.darkBlue} points="36.2 42.15 33.99 38.53 32.73 42 31.45 45.5 30.09 49.23 30.08 57.63 30.55 57.63 34.18 47.68 35.46 44.18 36.2 42.15" />
        <polygon fill={BRAND.blue} points="30.09 44.41 30.09 41.4 30.09 40.7 30.1 39.9 28.77 38.83 28.72 38.97 27.51 42.26 27.49 42.33 26.56 44.87 26.55 57.63 27.02 57.63 27.02 57.63 28.45 57.63 30.08 57.63 30.08 57.63 30.09 49.23 30.09 44.41" />
        <polygon fill={BRAND.grey} points="20.82 37.93 20.28 38.36 17.92 40.24 17.92 41.98 21.45 40.79 21.45 37.43 20.82 37.93" />
        <polygon fill={BRAND.grey} points="21.44 57.62 21.44 57.62 18.93 57.62 21.44 57.62" />
        <polygon fill={BRAND.grey} points="21.45 44.29 17.92 45.47 17.91 57.62 18.38 57.62 21.44 49.22 21.45 44.29" />
        <polygon fill={BRAND.darkBlue} points="23.49 43.61 24.95 39.62 21.45 40.79 17.92 41.98 14.15 43.24 7.58 48.47 7.87 48.84 17.92 45.47 21.45 44.29 23.49 43.61" />
        <polygon fill={BRAND.blue} points="17.92 40.24 20.28 38.36 20.82 37.93 21.45 37.43 21.46 35.72 21.32 35.77 18 36.88 17.93 36.91 15.36 37.76 5.38 45.71 7.58 48.47 14.15 43.24 17.92 40.24" />
        <polygon fill={BRAND.petal} points="18 36.88 21.32 35.77 21.46 35.72 21.46 35.17 21.45 35.17 21.45 35.17 19.89 34.16 18.46 33.23 14.44 34.58 8.77 36.48 .29 39.32 .29 39.32 .92 40.11 2.49 42.08 15.36 37.76 17.93 36.91 18 36.88" />
        <polygon fill={BRAND.grey} points="12.63 33.41 9.5 31.38 0 38.95 .29 39.32 8.77 36.48 12.63 33.41" />
        <polygon fill={BRAND.grey} points="17.21 28.95 16.54 28.8 13.6 28.12 12.24 29.21 15.36 31.23 18 29.13 17.21 28.95" />
        <polygon fill={BRAND.darkBlue} points="14.44 34.58 18.46 33.23 15.36 31.23 12.24 29.21 8.9 27.04 .71 25.17 1.4 22.19 .61 25.63 9.5 31.38 12.63 33.41 14.44 34.58" />
        <polygon fill={BRAND.blue} points="8.9 27.04 13.6 28.12 16.54 28.8 17.21 28.95 18 29.13 19.33 28.07 19.21 27.99 16.27 26.09 16.21 26.05 13.94 24.58 1.5 21.73 1.4 22.19 .71 25.17 8.9 27.04" />
        <polygon fill={BRAND.petal} points="16.27 26.09 19.21 27.99 19.33 28.07 19.76 27.73 19.76 27.72 19.76 27.72 19.58 25.87 19.41 24.17 15.85 21.87 10.83 18.62 3.33 13.76 2.7 16.49 2.64 16.74 2.54 17.2 13.94 24.58 16.21 26.05 16.27 26.09" />
        <polygon fill={BRAND.grey} points="15.64 19.72 15.27 16.02 3.43 13.3 3.33 13.76 10.83 18.62 15.64 19.72" />
        <polygon fill={BRAND.grey} points="21.98 20.53 21.68 19.9 20.38 17.19 18.68 16.8 19.05 20.5 22.33 21.26 21.98 20.53" />
        <polygon fill={BRAND.darkBlue} points="15.85 21.87 19.41 24.17 19.05 20.5 18.68 16.8 18.29 12.84 14.65 5.27 17.41 3.95 14.23 5.47 15.27 16.02 15.64 19.72 15.85 21.87" />
        <polygon fill={BRAND.blue} points="18.29 12.84 20.38 17.19 21.68 19.9 21.98 20.53 22.33 21.26 23.99 21.64 23.98 21.49 23.63 18.01 23.62 17.93 23.36 15.24 17.83 3.74 17.41 3.95 14.65 5.27 18.29 12.84" />
        <polygon fill={BRAND.petal} points="23.63 18.01 23.98 21.49 23.99 21.64 24.53 21.76 24.53 21.76 24.53 21.76 25.86 20.46 27.09 19.27 26.67 15.05 26.08 9.1 25.2 .2 22.02 1.73 23.36 15.24 23.62 17.93 23.63 18.01" />
        <polygon fill={BRAND.grey} points="28.21 13.54 30.88 10.95 25.62 0 25.2 .2 26.08 9.1 28.21 13.54" />
        <polygon fill={BRAND.grey} points="31.54 19 31.84 18.38 33.15 15.67 32.4 14.1 29.73 16.7 31.19 19.73 31.54 19" />
        <polygon fill={BRAND.darkBlue} points="26.67 15.05 27.09 19.27 29.73 16.7 32.4 14.1 35.25 11.33 38.9 3.76 38.47 3.56 30.88 10.95 28.21 13.54 26.67 15.05" />
        <polygon fill={BRAND.blue} points="33.15 15.67 31.84 18.38 31.54 19 31.19 19.73 31.93 21.27 32.03 21.16 34.54 18.72 34.59 18.67 36.53 16.79 42.07 5.3 41.65 5.09 39.98 4.29 38.9 3.76 35.25 11.33 33.15 15.67" />
        <polygon fill={BRAND.petal} points="34.54 18.72 32.03 21.16 31.93 21.27 32.16 21.76 32.17 21.76 32.17 21.77 34.01 22 35.71 22.21 38.74 19.25 43.03 15.08 49.43 8.85 46.68 7.52 46.26 7.31 36.53 16.79 34.59 18.67 34.54 18.72" />
        <polygon fill={BRAND.grey} points="38.69 25.53 39.36 25.38 42.3 24.71 43.06 23.14 39.37 22.67 37.9 25.71 38.69 25.53" />
        <polygon fill={BRAND.grey} points="43.03 15.08 40.89 19.52 44.58 19.99 49.86 9.05 46.68 7.52 49.43 8.85 43.03 15.08" />
        <polygon fill={BRAND.darkBlue} points="38.74 19.25 35.71 22.21 39.37 22.67 43.06 23.14 47 23.64 55.19 21.78 55.09 21.32 44.58 19.99 40.89 19.52 38.74 19.25" />
        <polygon fill={BRAND.blue} points="42.3 24.71 39.36 25.38 38.69 25.53 37.9 25.71 37.16 27.24 37.31 27.26 40.78 27.7 40.85 27.71 43.53 28.05 55.98 25.22 55.19 21.78 47 23.64 42.3 24.71" />
        <polygon fill={BRAND.petal} points="40.78 27.7 37.31 27.26 37.16 27.24 36.92 27.74 36.92 27.74 36.92 27.74 37.89 29.33 38.78 30.79 42.99 31.32 48.92 32.07 57.79 33.19 57.11 30.21 57.01 29.75 43.53 28.05 40.85 27.71 40.78 27.7" />
        <polygon fill={BRAND.grey} points="38.05 35.19 38.59 35.62 40.94 37.5 42.64 37.11 40.7 33.94 37.42 34.68 38.05 35.19" />
        <polygon fill={BRAND.grey} points="48.92 32.07 44.11 33.16 46.05 36.34 57.89 33.65 57.11 30.21 57.79 33.19 48.92 32.07" />
        <polygon fill={BRAND.darkBlue} points="44.11 33.16 42.99 31.32 38.78 30.79 40.7 33.94 42.64 37.11 44.71 40.51 51.27 45.75 51.56 45.38 46.05 36.34 44.11 33.16" />
        <polygon fill={BRAND.blue} points="40.94 37.5 38.59 35.62 38.05 35.19 37.42 34.68 35.76 35.06 35.83 35.18 37.65 38.17 37.69 38.24 39.1 40.54 49.07 48.51 49.36 48.14 49.36 48.14 51.27 45.75 44.71 40.51 40.94 37.5" />
        <polygon fill={BRAND.petal} points="37.65 38.17 35.83 35.18 35.76 35.06 35.22 35.18 35.22 35.18 35.21 35.18 34.58 36.93 33.99 38.53 36.2 42.15 39.31 47.26 43.97 54.89 44.29 54.49 45.87 52.5 46.17 52.14 39.1 40.54 37.69 38.24 37.65 38.17" />
        <polygon fill={BRAND.grey} points="35.46 44.18 34.18 47.68 43.67 55.26 43.97 54.89 39.31 47.26 35.46 44.18" />
        <polygon fill={BRAND.grey} points="30.09 40.7 30.09 41.4 30.09 44.41 31.45 45.5 32.73 42 30.1 39.9 30.09 40.7" />
        <polygon fill={BRAND.petal} points="27.51 42.26 28.72 38.97 28.77 38.83 28.33 38.49 28.33 38.49 28.33 38.49 26.57 39.08 24.95 39.62 23.49 43.61 21.44 49.22 18.38 57.62 18.38 57.62 18.93 57.62 21.44 57.62 21.91 57.62 26.56 44.87 27.49 42.33 27.51 42.26" />
      </g>
    </svg>
  )
}
